import React, { useState, useEffect, useCallback, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  LinearProgress,
  Typography,
  Box,
  CircularProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Refresh } from "@material-ui/icons";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      minWidth: "400px",
      borderRadius: "12px",
      padding: theme.spacing(2),
    },
  },
  title: {
    textAlign: "center",
    color: theme.palette.primary.main,
    fontWeight: "bold",
    marginBottom: theme.spacing(2),
  },
  content: {
    textAlign: "center",
    padding: theme.spacing(2),
  },
  progressContainer: {
    margin: theme.spacing(3, 0),
  },
  progressBar: {
    height: "8px",
    borderRadius: "4px",
    backgroundColor: "#e0e0e0",
    "& .MuiLinearProgress-bar": {
      borderRadius: "4px",
      backgroundColor: theme.palette.primary.main,
    },
  },
  progressText: {
    marginTop: theme.spacing(1),
    fontSize: "14px",
    color: theme.palette.text.secondary,
  },
  statusText: {
    marginTop: theme.spacing(2),
    fontSize: "16px",
    fontWeight: "500",
    color: theme.palette.text.primary,
  },
  iconContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  refreshIcon: {
    fontSize: "48px",
    color: theme.palette.primary.main,
    animation: "$spin 2s linear infinite",
  },
  "@keyframes spin": {
    "0%": {
      transform: "rotate(0deg)",
    },
    "100%": {
      transform: "rotate(360deg)",
    },
  },
  successIcon: {
    fontSize: "48px",
    color: "#4caf50",
  },
  errorIcon: {
    fontSize: "48px",
    color: "#f44336",
  },
  actions: {
    justifyContent: "center",
    padding: theme.spacing(2),
  },
  closeButton: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
}));

const ReconnectProgressModal = ({ 
  open, 
  onClose, 
  whatsAppName = "WhatsApp",
  whatsAppId = null
}) => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState("Iniciando reconexión...");
  const [isComplete, setIsComplete] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // ✅ FUNCIÓN PARA VERIFICAR ESTADO REAL DE CONEXIÓN
  const checkConnectionStatus = useCallback(async () => {
    try {
      const response = await fetch(`/api/whatsapp/${whatsAppId}`);
      if (response.ok) {
        const data = await response.json();
        return data.status === "CONNECTED";
      }
      return false;
    } catch (error) {
      console.error("Error verificando estado de conexión:", error);
      return false;
    }
  }, [whatsAppId]);

  // ✅ ESCUCHAR EVENTOS DE SOCKET PARA DETECTAR CONEXIÓN INMEDIATAMENTE
  useEffect(() => {
    if (!open || !whatsAppId || !socket || !user?.companyId) return;

    // ✅ EXTRAER companyId A VARIABLE PARA EVITAR EXPRESIÓN COMPLEJA EN DEPENDENCIAS
    const companyId = user.companyId;
    
    const handleConnectionSuccess = (data) => {
      // ✅ DETECTAR CONEXIÓN EXITOSA INMEDIATAMENTE
      if (
        (data.action === "connected" || data.action === "update") &&
        ((data.whatsapp && data.whatsapp.id === whatsAppId && data.whatsapp.status === "CONNECTED") ||
         (data.session && data.session.id === whatsAppId && data.session.status === "CONNECTED"))
      ) {
        console.log("✅ Conexión detectada inmediatamente desde socket - Actualizando modal");
        setProgress(100);
        setIsComplete(true);
        setStatus("¡Reconexión completada exitosamente!");
        
        // ✅ ACTUALIZAR PÁGINA AUTOMÁTICAMENTE DESPUÉS DE CONFIRMAR CONEXIÓN
        setTimeout(() => {
          console.log("🔄 Actualizando página automáticamente...");
          window.location.reload();
        }, 1500); // Reducido a 1.5 segundos para mejor UX
      }
    };

    // ✅ REGISTRAR LISTENERS
    socket.on(`company-${companyId}-whatsapp`, handleConnectionSuccess);
    socket.on(`company-${companyId}-whatsappSession`, handleConnectionSuccess);

    return () => {
      // ✅ CLEANUP
      if (socket && typeof socket.off === 'function') {
        socket.off(`company-${companyId}-whatsapp`, handleConnectionSuccess);
        socket.off(`company-${companyId}-whatsappSession`, handleConnectionSuccess);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, whatsAppId, socket, user.companyId]);

  useEffect(() => {
    if (open && whatsAppId) {
      // Resetear estado al abrir
      setProgress(0);
      setIsComplete(false);
      setHasError(false);
      setErrorMessage("");
      setStatus("Iniciando reconexión...");
      
      let progressValue = 0;
      let attempts = 0;
      const maxAttempts = 40; // Máximo 30 intentos (30 segundos)
      
      // ✅ VALIDACIÓN REAL CON BACKEND
      const progressInterval = setInterval(async () => {
        attempts++;
        progressValue = Math.min((attempts / maxAttempts) * 100, 95); // Máximo 95% hasta confirmar
        setProgress(progressValue);
        
        // Actualizar mensaje según progreso
        if (progressValue < 30) {
          setStatus("Conectando con WhatsApp...");
        } else if (progressValue < 60) {
          setStatus("Verificando sesión...");
        } else if (progressValue < 90) {
          setStatus("Sincronizando datos...");
        } else {
          setStatus("Validando conexión...");
        }
        
        // ✅ VERIFICAR ESTADO REAL CADA 2 INTENTOS (cada 2 segundos) - Reducido para mejor respuesta
        if (attempts % 2 === 0) {
          try {
            const isConnected = await checkConnectionStatus();
            if (isConnected) {
              clearInterval(progressInterval);
              setProgress(100);
              setIsComplete(true);
              setStatus("¡Reconexión completada exitosamente!");
              
              // ✅ ACTUALIZAR PÁGINA AUTOMÁTICAMENTE DESPUÉS DE CONFIRMAR CONEXIÓN
              setTimeout(() => {
                console.log("🔄 Actualizando página automáticamente...");
                window.location.reload();
              }, 1500); // Reducido a 1.5 segundos para mejor UX
              
              return;
            }
          } catch (error) {
            console.error("Error verificando conexión:", error);
          }
        }
        
        // ✅ TIMEOUT DESPUÉS DE MÁXIMOS INTENTOS
        if (attempts >= maxAttempts) {
          clearInterval(progressInterval);
          setHasError(true);
          setErrorMessage("La reconexión está tomando más tiempo del esperado. Por favor, verifique manualmente el estado de la conexión.");
          setStatus("Reconexión en progreso...");
        }
      }, 1000); // Verificar cada segundo

      return () => {
        clearInterval(progressInterval);
      };
    }
  }, [open, whatsAppId, checkConnectionStatus]);

  const handleClose = (event, reason) => {
    // ✅ NUEVA API: Filtrar eventos de backdrop click cuando no se puede cerrar
    if (reason === 'backdropClick' && !isComplete && !hasError) {
      return; // No cerrar si está en proceso
    }
    
    if (isComplete || hasError) {
      onClose();
    }
  };

  const getStatusIcon = () => {
    if (hasError) {
      return <CircularProgress className={classes.errorIcon} />;
    }
    if (isComplete) {
      return <CircularProgress className={classes.successIcon} />;
    }
    return <Refresh className={classes.refreshIcon} />;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      className={classes.dialog}
      disableEscapeKeyDown={!isComplete && !hasError}
    >
      <DialogTitle className={classes.title}>
        Reconectando {whatsAppName}
      </DialogTitle>
      
      <DialogContent className={classes.content}>
        <Box className={classes.iconContainer}>
          {getStatusIcon()}
        </Box>
        
        <Typography className={classes.statusText}>
          {status}
        </Typography>
        
        <Box className={classes.progressContainer}>
          <LinearProgress
            variant="determinate"
            value={progress}
            className={classes.progressBar}
          />
          <Typography className={classes.progressText}>
            {Math.round(progress)}% completado
          </Typography>
        </Box>
        
        {isComplete && (
          <Typography 
            style={{ 
              color: "#4caf50", 
              fontWeight: "bold", 
              marginTop: "16px" 
            }}
          >
            <span role="img" aria-label="checkmark">✅</span> Actualizando página automáticamente...
          </Typography>
        )}
        
        {hasError && errorMessage && (
          <Typography 
            style={{ 
              color: "#f44336", 
              fontWeight: "bold", 
              marginTop: "16px",
              fontSize: "14px"
            }}
          >
            <span role="img" aria-label="warning">⚠️</span> {errorMessage}
          </Typography>
        )}
      </DialogContent>
      
      <DialogActions className={classes.actions}>
        {(isComplete || hasError) && (
          <Button
            onClick={handleClose}
            variant="contained"
            className={classes.closeButton}
          >
            {isComplete ? "Cerrar" : "Entendido"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ReconnectProgressModal;
